import "./App.scss";
import { ExperimentContext } from "./utils/experiment-context";
import { Location, Redirect, Router } from "@reach/router";
import { LocationContext } from "./utils/hooks/useLocation";
import { Mixpanel } from "./utils/mixpanelWrapper";
import { Page } from "./Components/Page";
import { ProtectedRoute, ProtectedRouteWithRedirects } from "./Components/ProtectedRoute";
import { useCompanyInfo, useCompanyInfoFetch } from "./redux/company";
import { useSelector } from "react-redux";
import * as R from "ramda";
import * as UserRedux from "./redux/user";
import Admin from "./Admin";
import BasicFaq from "./MMM/BasicFaq";
import CompanyRoutes from "./CompanyRoutes";
import { detectAndPurgeDuplicateAuthCookies } from "./utils/auth";
import DemoDataGenerator from "./DemoDataGenerator";
import DagGraphs from "./DagGraphs";
import DataFreshness from "./DataFreshness";
import IncrementalityJobStatus from "./IncrementalityJobStatus";
import LinearClearanceAssumptions from "./LinearClearanceAssumptions";
import NavBar from "./NavBar";
import OldExpectedBookings from "./ExpectedBookings/OldExpectedBookings";
import React, { useEffect, useMemo } from "react";
import UserAdmin from "./UserAdmin";
import { MigrationPage } from "./MigrationPage";

// Drain users whose cookie jars carry stale host-only auth cookies from older
// builds alongside the current domain-scoped ones — js-cookie would otherwise
// read the stale copy on every request and trip the auth-refresh loop. Runs
// once at module load, before any auth-reading code mounts.
detectAndPurgeDuplicateAuthCookies();

// Value of LocationContext is constantly provided by Reach Router
// https://reach.tech/router/api/Location
const LocationWrapper = () => (
  <Location>
    {({ location }) => (
      <LocationContext.Provider value={location}>
        <MainApp location={location} />
      </LocationContext.Provider>
    )}
  </Location>
);

const MainApp = ({ location }) => {
  useCompanyInfoFetch();
  const { cid, experiments: pgCompanyExperimentsRaw } = useCompanyInfo();
  const userEmail = useSelector(UserRedux.emailSelector);
  let userRole = useSelector(UserRedux.roleSelector);
  const isInternal = useSelector(UserRedux.isInternalSelector);

  const isAdmin = useSelector(UserRedux.isAdminSelector);
  const pgUserExperiments = useSelector(UserRedux.userExperimentsSelector);

  // R.mergeWith returns a new object every call, so memoize to keep the context value stable.
  // Also avoid mutating the Redux-stored pgCompanyExperimentsRaw array.
  const experiments = useMemo(() => {
    // TODO: Figure out better solution when we return alternative cid for tinuititest
    const pgCompanyExperiments =
      cid === "tinuititest"
        ? [
            ...(pgCompanyExperimentsRaw || []),
            "enableBrandHealthMetrics",
            "enableGeoMMM",
            "enableCustomerInsights",
            "enableBrandEquityNavbar",
          ]
        : pgCompanyExperimentsRaw || [];
    return R.mergeWith(
      R.or,
      R.fromPairs(R.map(elem => [elem, true], pgCompanyExperiments)),
      R.fromPairs(R.map(elem => [elem, true], pgUserExperiments || []))
    );
  }, [cid, pgCompanyExperimentsRaw, pgUserExperiments]);

  // If we have override role active, we need this to not gate off the user admin page
  const trueRole = useSelector(UserRedux.trueRoleSelector);

  useEffect(() => {
    if (userEmail) {
      Mixpanel.identify(`${userEmail}`);
      Mixpanel.people.set({
        distinct_id: `${userEmail}`,
        $email: userEmail,
        role: userRole,
        isInternal: isInternal,
        isAdmin: isAdmin,
        company: cid,
      });
    }
  }, [userEmail, isInternal, isAdmin, userRole, cid]);

  return (
    <ExperimentContext.Provider value={experiments}>
      {/* TODO: we need to make a wrapper for "NonCompanyRoutes" and give it its own navbar that has no logo etc. For a company it needs some metadata */}
      <NavBar location={location} />
      <div className="main">
        <Router className="bpmRouter">
          <MigrationPage key="companies root" path="/" title="Companies" pageType="Companies" />
          <MigrationPage key="companies" path="/companies" title="Companies" pageType="Companies" />
          <ProtectedRouteWithRedirects
            key="dag-status"
            path="/dag-status"
            component={() => <MigrationPage title="Dag Status" pageType="Dag Status" />}
            roleRequired={3}
            redirects={["/dag_status"]}
          />
          <ProtectedRouteWithRedirects
            key="kpi-status"
            path="/kpi-status"
            component={() => <MigrationPage title="KPI Status" pageType="KPI Status" />}
            redirects={["/kpi_status"]}
          />
          <ProtectedRouteWithRedirects
            key="site-monitoring"
            path="/site-monitoring"
            component={() => <MigrationPage title="Site Monitoring" pageType="Site Monitoring" />}
            roleRequired={2}
            redirects={["/sitemonitoring", "/site_monitoring"]}
          />
          <ProtectedRouteWithRedirects
            key="feed-management"
            path="/feed-management"
            component={() => <MigrationPage title="Feed Management" pageType="Feed Management" />}
            roleRequired={2}
            redirects={["/feedmanagement", "/feed_management"]}
          />
          <ProtectedRouteWithRedirects
            key="commerce-scraper"
            path="/commerce-scraper"
            component={() => <MigrationPage title="Commerce Scraper" pageType="Commerce Scraper" />}
            roleRequired={2}
            redirects={["/commercescraper", "/commerce_scraper"]}
          />
          <ProtectedRouteWithRedirects
            key="batch-failures"
            path="/batch-failures"
            component={() => <MigrationPage title="Batch Failures" pageType="Batch Failures" />}
            roleRequired={3}
            redirects={["/batchfailures", "/batch_failures"]}
          />
          <ProtectedRouteWithRedirects
            key="system-health"
            path="/system-health"
            component={() => <MigrationPage title="System Health" pageType="System Health" />}
            roleRequired={1}
          />
          <ProtectedRouteWithRedirects
            key="api-sentry"
            path="/api-sentry"
            component={() => <MigrationPage title="API Sentry" pageType="API Sentry" />}
            roleRequired={1}
          />
          <ProtectedRouteWithRedirects
            key="incrementality"
            path="/incrementality-job-status"
            component={IncrementalityJobStatus}
            redirects={["/incrementality_job_status"]}
          />
          <ProtectedRouteWithRedirects
            key="dag-graphs"
            path="/dag-graphs"
            component={DagGraphs}
            roleRequired={3}
            redirects={["/dag_graphs"]}
          />
          <ProtectedRoute
            key="calendar"
            path="/calendar"
            component={() => <MigrationPage title="Calendar" pageType="Calendar" />}
          />
          <ProtectedRoute
            suspense
            key="process"
            path="process"
            component={() => <MigrationPage title="Process Logs" pageType="Process Logs" />}
          />
          <ProtectedRoute
            key="streamingnetworks"
            path="/streaming/networks/*"
            component={() => (
              <MigrationPage title="Streaming Networks" pageType="Streaming Networks" />
            )}
          />
          <ProtectedRoute
            key="activeott"
            path="/activeott/*"
            component={() => <MigrationPage title="ActiveOTT" pageType="ActiveOTT" />}
          />
          <ProtectedRoute
            key="linearbuyingassignments"
            path="/linear/assignments"
            component={() => (
              <MigrationPage
                title="Linear Buying Assignments"
                pageType="Linear Buying Assignments"
              />
            )}
          />
          <ProtectedRouteWithRedirects
            key="accountplanning"
            path="/account-planning"
            component={() => <MigrationPage title="Account Planning" pageType="Account Planning" />}
            redirects={["/accountplanning", "/account_planning"]}
          />
          <ProtectedRoute
            key="oldexpectedbookings"
            path="/bookings"
            component={OldExpectedBookings}
          />
          <ProtectedRouteWithRedirects
            key="devicebreakout"
            path="/device-breakout"
            component={() => <MigrationPage title="Device Breakout" pageType="Device Breakout" />}
            redirects={["/devicebreakout"]}
          />
          <ProtectedRoute
            key="linearconstraints"
            path="/linear/constraints"
            component={() => (
              <MigrationPage
                title="Linear Global Constraints"
                pageType="Linear Global Constraints"
              />
            )}
          />
          <ProtectedRoute
            key="streamingconstraints"
            path="/streaming/constraints"
            component={() => (
              <MigrationPage
                title="Streaming Global Constraints"
                pageType="Streaming Global Constraints"
              />
            )}
          />
          <ProtectedRoute key="admin" path="/admin" component={Admin} />
          {/* The user admin page should be internal only but since Protected Route uses the regular role
          selector, set the required role to 10 and block the page using trueRole */}
          {trueRole < 2 && (
            <ProtectedRoute
              key="useradmin"
              path="/user-admin"
              component={UserAdmin}
              roleRequired={10}
            />
          )}
          <Redirect noThrow from="/useradmin" to="/user-admin" />
          <Redirect noThrow from="/user_admin" to="/user-admin" />
          <ProtectedRoute
            key="linearcpms"
            path="/linear/cpms"
            component={() => <MigrationPage title="Linear CPMs" pageType="Linear CPMs" />}
          />
          <ProtectedRoute
            key="linearclearanceassumptions"
            path="/linear/assumptions"
            component={LinearClearanceAssumptions}
          />
          <ProtectedRouteWithRedirects
            key="linearcanonicalnetworks"
            path="/linear/networks"
            component={() => (
              <MigrationPage
                title="Linear Canonical Networks"
                pageType="Linear Canonical Networks"
              />
            )}
            redirects={["/linear/rates", "/linear/contacts"]}
          />
          <ProtectedRoute
            key="iconlocalmappings"
            path="/linear/icon-local-mappings"
            component={() => (
              <MigrationPage
                title="Icon Local Linear Mappings"
                pageType="Icon Local Linear Mappings"
              />
            )}
          />
          <ProtectedRouteWithRedirects
            key="dag start"
            path="/dag-start"
            component={() => <MigrationPage title="Dag Start" pageType="Dag Start" />}
            roleRequired={[3]}
            redirects={["/dagstart", "/dag_start"]}
          />
          <ProtectedRoute
            suspense
            key="invoices2"
            path="/invoicing"
            component={() => <MigrationPage title="Invoicing" pageType="Invoicing" />}
          />
          <ProtectedRoute
            key="pacing streaming"
            path="/pacing/streaming/*"
            component={() => <MigrationPage title="Streaming Pacing" pageType="Streaming Pacing" />}
          />
          <ProtectedRoute
            key="pacing display"
            path="/pacing/display/"
            component={() => <MigrationPage title="Display Pacing" pageType="Display Pacing" />}
          />
          <ProtectedRoute
            key="streaming discrepancies"
            path="/pacing/discrepancies"
            component={() => (
              <MigrationPage title="Streaming Discrepancies" pageType="Streaming Discrepancies" />
            )}
          />
          <ProtectedRoute
            key="pacing linear"
            path="/pacing/linear"
            component={() => <MigrationPage title="Linear Pacing" pageType="Linear Pacing" />}
          />
          <ProtectedRoute
            key="logs tracker"
            path="/logs/*"
            component={() => <MigrationPage title="Logs Tracker" pageType="Logs Tracker" />}
          />
          <ProtectedRoute
            key="linear adops"
            path="/adops/linear"
            component={() => (
              <MigrationPage title="Linear AdOps Tools" pageType="Linear AdOps Tools" />
            )}
          />
          <ProtectedRoute
            key="streaming adops"
            path="/adops/streaming"
            component={() => (
              <MigrationPage title="Streaming AdOps Tools" pageType="Streaming AdOps Tools" />
            )}
          />
          <ProtectedRouteWithRedirects
            key="streaming adops validate placement"
            path="/adops/streaming/validate-placement"
            component={() => (
              <MigrationPage title="Streaming AdOps Tools" pageType="Streaming AdOps Tools" />
            )}
            redirects={["/validate-placements", "/validateplacements", "validateplacement"]}
          />
          <ProtectedRouteWithRedirects
            key="streaming adops mappings"
            path="/adops/streaming/mappings"
            component={() => (
              <MigrationPage title="Streaming AdOps Tools" pageType="Streaming AdOps Tools" />
            )}
            redirects={["/mapping"]}
          />
          <ProtectedRoute
            key="credit memos"
            path="/credit-memos"
            component={() => <MigrationPage title="Credit Memos" pageType="Credit Memos" />}
            redirects={["/creditmemos", "/credit_memos"]}
          />
          <ProtectedRouteWithRedirects
            key="add new client"
            path="/add-new-client"
            component={() => <MigrationPage title="Add New Client" pageType="Add New Client" />}
            redirects={["/addnewclient", "/add_new_client"]}
          />
          <ProtectedRouteWithRedirects
            key="kpi setup"
            path="/kpi-setup"
            component={() => <MigrationPage title="KPI Setup" pageType="KPI Setup" />}
            redirects={["/kpisetup", "/kpi_setup"]}
          />
          <ProtectedRoute
            component={() => <MigrationPage title="Reference Docs" pageType="Reference Docs" />}
            key="reference"
            path="/reference"
            roleRequired={10} // External Users
          />
          <ProtectedRouteWithRedirects
            key="query exporter"
            path="/query-exporter"
            component={() => <MigrationPage title="Query Exporter" pageType="Query Exporter" />}
            roleRequired={3}
            redirects={["/queryexporter", "/query_exporter"]}
          />
          <ProtectedRouteWithRedirects
            key="data connector tool"
            path="/data-connectors"
            component={() => (
              <MigrationPage title="Data Connector Tool" pageType="Data Connector Tool" />
            )}
            roleRequired={3}
            redirects={["/dataconnectors", "/data_connectors"]}
          />
          {trueRole < 2 && (
            <ProtectedRoute
              key="data freshness"
              path="admin/data-freshness"
              component={DataFreshness}
              roleRequired={10}
            />
          )}
          <ProtectedRoute
            key="Incrementality Playbook"
            path="/incrementality-playbook/*page"
            component={() => (
              <MigrationPage title="Incrementality Playbook" pageType="Incrementality Playbook" />
            )}
          />
          <ProtectedRoute
            key="Cross Channel Demo Data Generator"
            path="/demo-data-generator"
            component={DemoDataGenerator}
          />
          <BasicFaq key="mmm faq" path="/mmm/faq" />
          <Redirect noThrow key="pacing root" from="/pacing" to="/pacing/streaming" />
          <Redirect noThrow from="/companies/:company/*" to="/:company" />
          <Redirect noThrow from="/login" to="/" />
          <ProtectedRoute
            key="Image Uploader"
            path="/image-uploader"
            component={() => <MigrationPage title="Image Uploader" pageType="Image Uploader" />}
          />
          <ProtectedRoute
            key="slide-generator-admin"
            path="/slide-generator-admin"
            component={() => (
              <MigrationPage title="Slide Generator Admin" pageType="Slide Generator Admin" />
            )}
            roleRequired={1}
          />
          <ProtectedRoute
            key="product-roadmap"
            path="/product-roadmap"
            component={() => <MigrationPage title="Product Roadmap" pageType="Product Roadmap" />}
            roleRequired={1}
          />
          <CompanyRoutes path="/:company/*" />
        </Router>
      </div>
    </ExperimentContext.Provider>
  );
};

// Shown when sign-in can't be reached by redirect (this page is the sign-in page's own '/').
const SignInLink = () => (
  <div className="bpm-sign-in" style={{ padding: 32, textAlign: "center" }}>
    <a href="/">Sign in</a>
  </div>
);

const App = () => {
  UserRedux.useVersionCheck();
  const isAuthed = UserRedux.useIsAuthed();
  const authFailure = useSelector(UserRedux.authFailureSelector);
  let content;
  // Nothing but the modals renders until a session has been established and /me has answered.
  if (isAuthed) {
    content = <LocationWrapper />;
  } else if (authFailure === UserRedux.BootOutcome.SIGN_IN_LINK) {
    content = <SignInLink />;
  } else {
    content = <Page onlyModals={true}></Page>;
  }
  return <div className="bpm">{content}</div>;
};

export default App;
